import { DEFAULT_LOCALE, LocaleSchema } from '@aysar/contracts';
import { z } from 'zod';

import {
  THEME_PREFERENCES,
  type PreferencesState,
} from '@/shared/store/reducers/preferences/preferences.types';

export const PREFERENCES_STORAGE_KEY = 'aysar.admin.preferences';

export const DEFAULT_PREFERENCES: PreferencesState = {
  theme: 'system',
  locale: DEFAULT_LOCALE,
  sidebarCollapsed: false,
  openNavGroups: [],
};

/**
 * Every field is optional and defaulted, and that is the OPPOSITE of
 * `auth.storage`'s strict, fail-closed parse — deliberately.
 *
 * A session that half-parses is a session that fails halfway through the
 * operator's afternoon, so it is refused whole. A *preference* that half-parses
 * costs nothing: an unreadable theme falls back to the OS and an unrecognised
 * locale falls back to Arabic, and the operator changes it again in one click.
 * Refusing the whole set instead would throw away a perfectly good sidebar
 * preference because a later release renamed a theme.
 */
const StoredPreferencesSchema = z.object({
  theme: z.enum(THEME_PREFERENCES).catch(DEFAULT_PREFERENCES.theme),
  locale: LocaleSchema.catch(DEFAULT_PREFERENCES.locale),
  sidebarCollapsed: z.boolean().catch(DEFAULT_PREFERENCES.sidebarCollapsed),
  openNavGroups: z.array(z.string()).catch([...DEFAULT_PREFERENCES.openNavGroups]),
});

/**
 * Read SYNCHRONOUSLY, before React mounts (`main.tsx`), so `<html>` already
 * carries the right `dir`, `lang` and `.dark` class on the first paint. An
 * async hydration here is the flash of a light Latin layout before an Arabic
 * dark one — on every single page load.
 */
export function readPreferencesFromStorage(): PreferencesState {
  let raw: string | null;

  try {
    raw = window.localStorage.getItem(PREFERENCES_STORAGE_KEY);
  } catch {
    return { ...DEFAULT_PREFERENCES };
  }

  if (raw === null) return { ...DEFAULT_PREFERENCES };

  try {
    return StoredPreferencesSchema.parse(JSON.parse(raw));
  } catch {
    return { ...DEFAULT_PREFERENCES };
  }
}

export function writePreferencesToStorage(state: PreferencesState): void {
  try {
    window.localStorage.setItem(PREFERENCES_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // A full or unavailable store costs the operator a preference, not a session.
  }
}
